import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { yesterdayLocalDate, timezoneAbbr } from '../lib/timezones';
import { reportCacheConfig } from '../lib/reportCache';
import { ReportShell, ReportView, formatLongDate, type ReportData } from './reports/ReportView';
import { exportDomAsHtml, exportDomAsPdf, slugify } from '../lib/exportDomReport';
import { ThreatHuntingReportView } from './reports/ThreatHuntingReportView';

interface Props { customerId?: string }

type ReportMode = 'soc' | 'hunt';

/**
 * Daily SOC Report tab - thin wrapper around the shared <ReportView /> that
 * handles the date picker + `/api/daily-report` fetch. The visual rendering
 * lives in `reports/ReportView.tsx` so the Weekly tab can re-use the exact
 * same HTML/CSS skeleton (matching the Logic-App email template).
 */
export function DailyReportTab({ customerId: customerIdProp }: Props = {}) {
  const { activeCustomerId, activeCustomer, customers } = useCustomers();
  const customerId = customerIdProp ?? activeCustomerId;
  const customer = customerIdProp
    ? customers.find((c) => c.id === customerIdProp)
    : activeCustomer;

  const [mode, setMode] = useState<ReportMode>('soc');

  // Reporting timezone resolves from the customer (falls back to UTC). Re-derive
  // whenever the customer changes so the date picker default tracks them.
  const customerTz = customer?.timezone ?? 'UTC';
  const [date, setDate] = useState<string>(() => yesterdayLocalDate(customerTz));

  useEffect(() => {
    setDate(yesterdayLocalDate(customerTz));
  }, [customerTz]);

  const dailyQuery = useQuery<ReportData, Error & { hint?: string }>({
    queryKey: ['daily-report', customerId, date],
    queryFn: () => apiFetch<ReportData>(
      `/daily-report?customerId=${encodeURIComponent(customerId ?? '')}&date=${encodeURIComponent(date)}`,
    ),
    enabled: !!customerId && customerId !== 'default' && mode === 'soc',
    ...reportCacheConfig('daily', date, customerTz),
  });
  const report = dailyQuery.data ?? null;
  const loading = dailyQuery.isLoading || dailyQuery.isFetching;
  const error: string | null = (!customerId || customerId === 'default')
    ? 'Pick a specific customer to generate the daily report. The MSSP "default" workspace is not supported here.'
    : dailyQuery.error
      ? (dailyQuery.error.hint ? `${dailyQuery.error.message}. ${dailyQuery.error.hint}` : dailyQuery.error.message)
      : null;

  const reportTz = report?.timezone ?? customerTz;
  const tzAbbr = timezoneAbbr(reportTz);
  const customerName = customer?.name ?? customerId ?? 'Customer';

  // Defined BEFORE any early returns so the hook order stays stable across
  // the "no customer selected" guard. Both HTML and PDF clone the on-screen
  // report DOM (with all page CSS inlined) rather than redrawing it, so the
  // export always matches whatever the customer actually sees.
  const exportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.daily-report-root',
      title: `${customerName} - Daily SOC Report - ${date}`,
      fileNameBase: `kloudynet-daily-soc-report-${slugify(customerName)}-${date}-${exportDateStr}`,
      notMountedError: 'Daily report content not visible - load a report first.',
    };
  }, [customerName, date]);

  const buildHtml = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    const exportedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    return exportDomAsHtml({
      ...exportOpts(),
      bannerHtml: `<div style="background:rgba(255,255,255,0.92);border-radius:12px;padding:14px 20px;margin-bottom:18px;color:#1e3c72;font-size:13px;box-shadow:0 4px 16px rgba(0,0,0,0.18);"><strong style="color:#0f172a;">${escapeHtml(customerName)}</strong> - Daily SOC Report &middot; Report date: <strong>${escapeHtml(date)}</strong> &middot; Exported ${escapeHtml(exportedAt)} UTC</div>`,
    });
  }, [report, exportOpts, customerName, date]);

  const buildPdf = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    return exportDomAsPdf(exportOpts());
  }, [report, exportOpts]);

  const huntExportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.threat-hunting-report-root',
      title: `${customerName} - Daily Threat Hunting Report - ${date}`,
      fileNameBase: `kloudynet-daily-threat-hunting-report-${slugify(customerName)}-${date}-${exportDateStr}`,
      notMountedError: 'Threat hunting report content not visible - load a report first.',
    };
  }, [customerName, date]);

  const buildHuntHtml = useCallback(async () => exportDomAsHtml(huntExportOpts()), [huntExportOpts]);
  const buildHuntPdf = useCallback(async () => exportDomAsPdf(huntExportOpts()), [huntExportOpts]);

  if (!customerId || customerId === 'default') {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-700 shadow-md">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Daily SOC Report</h2>
        <p className="text-sm">Select a specific customer from the dropdown above to generate their daily report.</p>
      </div>
    );
  }

  return (
    <>
      <div className="dr-no-print mb-3 flex gap-1 rounded-xl border border-border bg-background p-1 w-fit">
        <button
          type="button"
          onClick={() => setMode('soc')}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'soc' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
        >
          SOC Report
        </button>
        <button
          type="button"
          onClick={() => setMode('hunt')}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${mode === 'hunt' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
        >
          Threat Hunting Report
        </button>
      </div>

      {mode === 'hunt' ? (
        <ReportShell
          toolbarTitle="Threat Hunting Report"
          customerName={customerName}
          controls={null}
          loading={false}
          error={null}
          printDisabled={false}
          buildHtml={buildHuntHtml}
          buildPdf={buildHuntPdf}
        >
          <ThreatHuntingReportView customerId={customerId} customerLogoUrl={customer?.logoUrl} />
        </ReportShell>
      ) : (
        <ReportShell
          toolbarTitle="Daily SOC Report"
          customerName={customerName}
          controls={
            <label className="text-xs text-slate-600 dark:text-slate-300">
              <span className="block mb-1">Report date ({tzAbbr})</span>
              <input
                type="date"
                value={date}
                max={yesterdayLocalDate(reportTz)}
                onChange={(e) => setDate(e.target.value)}
                className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
              />
            </label>
          }
          loading={loading}
          error={error}
          printDisabled={!report || loading}
          buildHtml={buildHtml}
          buildPdf={buildPdf}
        >
          {report && !loading && (
            <ReportView
              report={report}
              customerName={customerName}
              title="Daily Security Intelligence Report"
              periodLine={`Report Period: ${formatLongDate(report.reportDate)}`}
              deltaWord="prior day"
              closedLabel="Incidents Closed (Previous Day)"
            />
          )}
        </ReportShell>
      )}
    </>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
